/**
 * useBattleSoundEffects
 *
 * Sound effects for the Fun Challenges battle. Mirrors the NSCL battle hook
 * (nscl/src/app/hooks/useBattleSoundEffects.ts), thin-wrapped over
 * `use-sound` (web) which is what cc-web-learning already standardises on.
 *
 * Exposed plays (all are no-ops when the audio fails to load — never block
 * gameplay):
 *   playCorrect   — square clicked correctly
 *   playWrong     — square clicked incorrectly
 *   playTimer     — once when timer reaches 6 s (caller decides when)
 *   playCelebrate — winner reveal
 *   playLost      — loser reveal
 */

import {useMemo} from 'react';
import useSound from 'use-sound';

import correctSound from '@/Assets/Sounds/correct-sound-square-trainer.mp3';
import wrongSound from '@/Assets/Sounds/wrong.mp3';
import timerSound from '@/Assets/Sounds/notification2.mp3';
import celebrateSound from '@/Assets/Sounds/levelup.mp3';
import lostSound from '@/Assets/Sounds/game_resign_countdown_sound.mp3';

export interface BattleSoundEffectsApi {
  playCorrect: () => void;
  playWrong: () => void;
  playTimer: () => void;
  playCelebrate: () => void;
  playLost: () => void;
}

function safe(fn?: () => void): () => void {
  return () => {
    try {
      fn?.();
    } catch {
      // Sound failures must never block UI.
    }
  };
}

export default function useBattleSoundEffects(): BattleSoundEffectsApi {
  const [doCorrect] = useSound(correctSound, {volume: 0.6});
  const [doWrong] = useSound(wrongSound, {volume: 0.6});
  const [doTimer] = useSound(timerSound, {volume: 0.5});
  const [doCelebrate] = useSound(celebrateSound, {volume: 0.7});
  const [doLost] = useSound(lostSound, {volume: 0.6});

  return useMemo(
    () => ({
      playCorrect: safe(doCorrect),
      playWrong: safe(doWrong),
      playTimer: safe(doTimer),
      playCelebrate: safe(doCelebrate),
      playLost: safe(doLost),
    }),
    [doCorrect, doWrong, doTimer, doCelebrate, doLost],
  );
}
